import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { User, Phone, MapPin, Users } from 'lucide-react';
import {
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
  VirtualizedFormSelect,
  ConfirmationDialog,
} from '@roger/ui-components';
import {
  updatePatient,
  getAvailableClinicians,
  getCurrentEpisode,
  updateCurrentEpisodeCaseManager,
  updateEpisodeAssignments,
  syncEpisodeNoaPhysician,
  checkEpisodeNoaPhysicianSync,
  listAgencyClinicPhysicians,
  checkPatientOasisMrnSync,
  syncPatientOasisMrn,
} from '../../services/api';
import type {
  PatientResponse,
  PatientUpdate,
  EmploymentStatus,
  Gender,
  MaritalStatus,
  AvailableClinicianResponse,
  ClinicPhysicianResponse,
} from '../../services/api/types.gen';
import { formatPhysicianName } from '../../utils/formatting';
import PhysicianInfoPopover from '../PhysicianInfoPopover';
import { ClinicianSelect } from '../clinician/ClinicianSelect';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import {
  parseISOToLocalDate,
  formatDateToISO,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import {
  formatSSN,
  formatStoredPhone,
  formatUsPhoneEntry,
  isCompleteUsPhone,
} from '../../utils/intake';
import {
  EMPLOYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  US_STATES,
} from '../../constants/options';
import { AutoSavePill } from '../AutoSavePill';
import { CHART_AUTOSAVE_DELAY_MS } from '../../hooks/useAutoSave';
import { PatientImportBadgeToggle } from './PatientImportBadgeToggle';
import { PatientTestStatusToggle } from './PatientTestStatusToggle';

interface PatientInfoTabProps {
  patient: PatientResponse;
  canEditPatientInfo: boolean;
  onPatientUpdated: (patient: PatientResponse) => void;
}

type FormState = {
  first_name: string;
  last_name: string;
  middle_name: string;
  suffix: string;
  date_of_birth: Date | undefined;
  gender: Gender | '';
  marital_status: MaritalStatus | '';
  employment_status: EmploymentStatus | '';
  primary_language: string;
  ssn: string;
  mbi: string;
  mrn: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  county: string;
  emergency_contact: string;
  emergency_contact_relationship: string;
  emergency_phone: string;
  secondary_contact: string;
  secondary_contact_relationship: string;
  secondary_phone: string;
  needs_legal_representative: boolean;
  has_poa: boolean;
};

type PendingSave = {
  patientId: string;
  form: FormState;
  // Revert-to-saved is not dirty vs lastSavedFormRef, but must still PUT so an
  // in-flight write of the abandoned value does not win in the database.
  force?: boolean;
  // A phone edit made while a PUT is in flight remains an explicit intent even
  // when the final value equals the saved baseline (for example, type then
  // clear). This keeps the queued PUT from omitting a required null.
  phoneFieldsChangedWhileSaving?: PhoneField[];
  // Same reason as the phone fields above: an MRN typed and then reverted
  // while a PUT was in flight is still intent to write, even though the
  // final value equals the baseline again.
  mrnChangedWhileSaving?: boolean;
};

function takePendingSave(ref: {
  current: PendingSave | null;
}): PendingSave | null {
  const queued = ref.current;
  ref.current = null;
  return queued;
}

function formatPatientPhone(value: string | null | undefined): string {
  // Keep malformed legacy values visible so hydration cannot silently discard
  // data. Valid stored values are normalized to the edit-form format.
  return formatStoredPhone(value) || value || '';
}

function patientToForm(patient: PatientResponse): FormState {
  return {
    first_name: patient.first_name ?? '',
    last_name: patient.last_name ?? '',
    middle_name: patient.middle_name ?? '',
    suffix: patient.suffix ?? '',
    date_of_birth: patient.date_of_birth
      ? (parseISOToLocalDate(patient.date_of_birth) ?? undefined)
      : undefined,
    gender: (patient.gender as Gender) ?? '',
    marital_status: patient.marital_status ?? '',
    employment_status: patient.employment_status ?? '',
    primary_language: patient.primary_language ?? '',
    ssn: patient.ssn ? formatSSN(patient.ssn) : '',
    mbi: patient.mbi ?? '',
    mrn: patient.mrn ?? '',
    phone: formatPatientPhone(patient.phone),
    email: patient.email ?? '',
    address: patient.address ?? '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    zip_code: patient.zip_code ?? '',
    county: patient.county ?? '',
    emergency_contact: patient.emergency_contact ?? '',
    emergency_contact_relationship:
      patient.emergency_contact_relationship ?? '',
    emergency_phone: formatPatientPhone(patient.emergency_phone),
    secondary_contact: patient.secondary_contact ?? '',
    secondary_contact_relationship:
      patient.secondary_contact_relationship ?? '',
    secondary_phone: formatPatientPhone(patient.secondary_phone),
    needs_legal_representative: patient.needs_legal_representative ?? false,
    has_poa: patient.has_poa ?? false,
  };
}

function formsDiffer(a: FormState, b: FormState): boolean {
  return (Object.keys(a) as (keyof FormState)[]).some(key => {
    const left = a[key];
    const right = b[key];
    if (left instanceof Date && right instanceof Date) {
      return left.getTime() !== right.getTime();
    }
    if (left instanceof Date || right instanceof Date) {
      return (left === undefined) !== (right === undefined);
    }
    return left !== right;
  });
}

const PHONE_FIELDS = ['phone', 'emergency_phone', 'secondary_phone'] as const;

type PhoneField = (typeof PHONE_FIELDS)[number];
const PHONE_ERROR_MESSAGE = 'Enter a 10-digit US phone number';

// Same rule the API enforces, so a bad MRN is refused at the keystroke rather
// than by a 422 after the debounce. Empty passes: an agency may hold a chart
// with no MRN, and clearing the field is a real edit.
const MRN_ERROR_MESSAGE =
  'MRN must contain only letters and numbers and cannot start with "test"';

function isValidMrn(value: string): boolean {
  const mrn = value.trim();
  if (!mrn) return true;
  return /^[A-Za-z0-9]+$/.test(mrn) && !mrn.toLowerCase().startsWith('test');
}

// Autosave must not fire on a half-typed MRN, but it must also not stall on
// one the user has not touched: a legacy value that would fail the rule today
// is still allowed to sit there while they edit their address.
function hasValidChangedMrn(
  form: FormState,
  lastSavedForm: FormState
): boolean {
  return form.mrn === lastSavedForm.mrn || isValidMrn(form.mrn);
}

// Send the MRN only when it changed. Imported charts carry MRNs the current
// rule would reject (hyphens, dots, a "test" prefix), and the API validates
// whatever it is given, so putting an untouched legacy value in every body
// would 422 the patient's whole form on any edit.
function getChangedMrnUpdate(
  form: FormState,
  lastSavedForm: FormState,
  explicitlyChanged = false
): Pick<PatientUpdate, 'mrn'> {
  if (form.mrn === lastSavedForm.mrn && !explicitlyChanged) return {};
  return { mrn: form.mrn.trim() || null };
}

// Signatures are mentioned only when there are some. Of the statuses that are
// still editable, only a chart QA is mid-correction on can carry one, so the
// count is usually zero and naming it every time would suggest otherwise.
function m0020SyncMessage(
  sync: { visitIds: string[]; signatureCount: number } | null
): string {
  if (!sync) return '';
  const visits = sync.visitIds.length;
  const visitText = `${visits} ${visits === 1 ? 'visit' : 'visits'}`;
  const base = `The OASIS Patient ID (M0020) still shows the previous MRN on ${visitText}. Update ${visits === 1 ? 'it' : 'them'} to the new MRN?`;
  if (sync.signatureCount === 0) return base;
  const signatures = `${sync.signatureCount} ${
    sync.signatureCount === 1 ? 'signature' : 'signatures'
  }`;
  return `${base} This will invalidate ${signatures} that must then be re-signed.`;
}

function hasValidChangedPhones(
  form: FormState,
  lastSavedForm: FormState
): boolean {
  return PHONE_FIELDS.every(
    field =>
      form[field] === lastSavedForm[field] || isCompleteUsPhone(form[field])
  );
}

function getChangedPhoneUpdates(
  form: FormState,
  lastSavedForm: FormState,
  explicitlyChangedFields: readonly PhoneField[] = []
): Pick<PatientUpdate, PhoneField> {
  const updates: Pick<PatientUpdate, PhoneField> = {};
  for (const field of PHONE_FIELDS) {
    if (
      form[field] !== lastSavedForm[field] ||
      explicitlyChangedFields.includes(field)
    ) {
      updates[field] = form[field].trim() || null;
    }
  }
  return updates;
}

// Radix rejects an empty SelectItem value, so an optional dropdown needs a
// sentinel to offer "no answer" back after something has been picked.
const NONE_VALUE = '__none__';

// Column widths on the patients table, except for suffix, which the API caps at
// the narrower width the 837I name segments allow. The API enforces the same
// caps and answers 422, but stopping the keystroke is quicker feedback than a
// round trip.
const MAX_LENGTHS = {
  mrn: 100,
  suffix: 10,
  county: 100,
  contactName: 255,
  contactRelationship: 100,
} as const;

export const PatientInfoTab: React.FC<PatientInfoTabProps> = ({
  patient,
  canEditPatientInfo,
  onPatientUpdated,
}) => {
  const { error: showError, success: showSuccess } = useToast();
  const [form, setForm] = useState<FormState>(() => patientToForm(patient));
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'unsaved' | 'saving' | 'saved' | 'error'
  >('idle');
  // Bumps on each debounced edit so the pill restarts its countdown bar.
  const [changeToken, setChangeToken] = useState(0);

  const [caseManagers, setCaseManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [currentCaseManagerId, setCurrentCaseManagerId] = useState<
    string | null
  >(null);
  const [episodeStartDate, setEpisodeStartDate] = useState<string | null>(null);
  const [episodeEndDate, setEpisodeEndDate] = useState<string | null>(null);
  const [physicianName, setPhysicianName] = useState<string | null>(null);
  const [currentPhysicianId, setCurrentPhysicianId] = useState<string | null>(
    null
  );
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [episodeId, setEpisodeId] = useState<string | null>(null);
  const [episodeLoaded, setEpisodeLoaded] = useState(false);
  // Physician just saved to the episode, pending the "also update the NOA?"
  // popup. Non-null means the popup is open; the id is passed explicitly to the
  // sync endpoint so a concurrent reassignment by someone else can't be applied
  // to the NOA by mistake.
  const [noaSyncPhysicianId, setNoaSyncPhysicianId] = useState<string | null>(
    null
  );
  const [isSyncingNoaPhysician, setIsSyncingNoaPhysician] = useState(false);

  // Charts still answering OASIS M0020 with the previous MRN, pending the
  // "also update them?" popup. Non-null means the popup is open. The MRN the
  // user was shown travels with it and is sent on confirm, so if the patient's
  // MRN has moved again in the meantime the server refuses rather than writing
  // a value nobody agreed to.
  const [m0020Sync, setM0020Sync] = useState<{
    mrn: string;
    visitIds: string[];
    signatureCount: number;
  } | null>(null);
  const [isSyncingM0020, setIsSyncingM0020] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFormRef = useRef(form);
  latestFormRef.current = form;
  const isMountRef = useRef(true);
  // Skip the debounce effect when we apply a current-generation save echo;
  // that setForm is not a user edit.
  const skipAutosaveRef = useRef(false);
  // Dirty checks compare against this, not the parent `patient` prop: the
  // parent can lag (or canonicalize) without meaning the user has a new edit.
  const lastSavedFormRef = useRef(patientToForm(patient));
  const patientIdRef = useRef(patient.id);
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef<PendingSave | null>(null);
  // Keep phone edits made during an in-flight PUT as explicit queued intent.
  // Comparing only with lastSavedFormRef loses type-then-clear because the
  // cleared value can equal the baseline again.
  const phoneFieldsChangedWhileSavingRef = useRef<Set<PhoneField>>(new Set());
  const mrnChangedWhileSavingRef = useRef(false);
  // Bumps on every user edit, including a revert, so a stale in-flight
  // completion cannot report "saved" over newer intent.
  const editGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    const next = patientToForm(patient);
    if (patient.id !== patientIdRef.current) {
      setForm(next);
      lastSavedFormRef.current = next;
      patientIdRef.current = patient.id;
      pendingSaveRef.current = null;
      phoneFieldsChangedWhileSavingRef.current.clear();
      mrnChangedWhileSavingRef.current = false;
      // The offer belongs to the patient it was made about. Left open across a
      // navigation it would name the previous chart's visits while pointing at
      // this one, which the server refuses but the user should never be asked.
      setM0020Sync(null);
      isMountRef.current = true;
      return;
    }
    // Same patient: adopt a parent echo only when the form is clean.
    // Dirty forms ignore it so a save snapshot cannot wipe in-progress
    // typing (ticket 28175 / EMR-1452). Clean forms must adopt, or a
    // remount after tab-switch hydrates a stale parent and the next
    // autosave overwrites the value that already made it to the server.
    if (formsDiffer(latestFormRef.current, lastSavedFormRef.current)) {
      return;
    }
    if (!formsDiffer(latestFormRef.current, next)) {
      return;
    }
    lastSavedFormRef.current = next;
    skipAutosaveRef.current = true;
    setForm(next);
  }, [patient]);

  useEffect(() => {
    let cancelled = false;
    setCaseManagers([]);
    setCurrentCaseManagerId(null);
    setEpisodeStartDate(null);
    setEpisodeEndDate(null);
    setPhysicianName(null);
    setCurrentPhysicianId(null);
    setPhysicians([]);
    setEpisodeId(null);
    setEpisodeLoaded(false);

    const fetchEpisodeAndCaseManagers = async () => {
      try {
        const episodeResponse = await getCurrentEpisode({
          path: { patient_id: patient.id },
        });

        if (cancelled) return;

        if (episodeResponse?.data) {
          setEpisodeId(episodeResponse.data.episode_id);
          setCurrentCaseManagerId(episodeResponse.data.case_manager_id ?? null);
          setEpisodeStartDate(episodeResponse.data.start_date ?? null);
          setEpisodeEndDate(episodeResponse.data.end_date ?? null);
          setPhysicianName(episodeResponse.data.physician_name ?? null);
          setCurrentPhysicianId(episodeResponse.data.physician_id ?? null);

          const clinicId = episodeResponse.data.agency_clinic_id;
          if (clinicId) {
            const [cliniciansResponse, physiciansResponse] = await Promise.all([
              getAvailableClinicians({
                query: { clinic_id: clinicId },
              }),
              listAgencyClinicPhysicians({
                path: { agency_clinic_id: clinicId },
              }),
            ]);
            if (!cancelled && cliniciansResponse?.data) {
              setCaseManagers(
                cliniciansResponse.data.filter(c => c.is_case_manager)
              );
            }
            if (!cancelled && physiciansResponse?.data) {
              setPhysicians(physiciansResponse.data);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching episode data:', err);
          showError('Unable to load episode data. Please try again.');
        }
      } finally {
        if (!cancelled) setEpisodeLoaded(true);
      }
    };

    fetchEpisodeAndCaseManagers();
    return () => {
      cancelled = true;
    };
  }, [patient.id]);

  const isDirty = useCallback((current: FormState) => {
    return formsDiffer(current, lastSavedFormRef.current);
  }, []);

  // Ask the server which charts the MRN change left behind. A failure here is
  // swallowed on purpose: the MRN itself already saved, and a popup that never
  // appears is a smaller problem than an error toast about a chart the user
  // was not asking about.
  const offerM0020Sync = useCallback(async (patientId: string, mrn: string) => {
    try {
      const check = await checkPatientOasisMrnSync({
        path: { patient_id: patientId },
      });
      const visitIds = check.data?.visit_ids ?? [];
      if (!visitIds.length || !mountedRef.current) return;
      if (patientIdRef.current !== patientId) return;
      setM0020Sync({
        mrn,
        visitIds,
        signatureCount: check.data?.signature_count ?? 0,
      });
    } catch (err: unknown) {
      console.error('Failed to check OASIS Patient ID sync:', err);
    }
  }, []);

  const handleConfirmM0020Sync = async () => {
    if (!m0020Sync) return;
    setIsSyncingM0020(true);
    try {
      const response = await syncPatientOasisMrn({
        path: { patient_id: patientIdRef.current },
        body: { visit_ids: m0020Sync.visitIds, mrn: m0020Sync.mrn },
      });
      const updated = response.data?.updated_visit_ids.length ?? 0;
      if (response.error || !response.data) {
        console.error('Failed to update OASIS Patient ID:', response.error);
        // A 409 here means the MRN moved again while the popup was open, and
        // saying so is more use than a generic failure.
        showError(
          getUserFacingApiMessage(
            response,
            'Failed to update the OASIS Patient ID'
          )
        );
      } else if (updated > 0) {
        showSuccess(
          `OASIS Patient ID updated on ${updated} ${
            updated === 1 ? 'visit' : 'visits'
          }`
        );
      } else {
        // Everything offered has since been signed, completed, or had its
        // period closed by someone else.
        showSuccess('Those visits can no longer be edited, so none changed');
      }
    } catch (err: unknown) {
      console.error('Failed to update OASIS Patient ID:', err);
      showError('Failed to update the OASIS Patient ID');
    } finally {
      setIsSyncingM0020(false);
      setM0020Sync(null);
    }
  };

  const doSave = useCallback(
    async (formSnapshot: FormState, opts?: Omit<PendingSave, 'form'>) => {
      if (!canEditPatientInfo) return;
      if (!formSnapshot.first_name.trim() || !formSnapshot.last_name.trim()) {
        return;
      }
      if (!hasValidChangedPhones(formSnapshot, lastSavedFormRef.current)) {
        return;
      }
      if (!hasValidChangedMrn(formSnapshot, lastSavedFormRef.current)) {
        return;
      }
      if (!opts?.force && !isDirty(formSnapshot)) return;

      const targetPatientId = opts?.patientId ?? patientIdRef.current;

      // One in-flight PUT at a time. Keep only the latest snapshot so an
      // older request cannot commit after a newer one (last-write-wins).
      // The queued intent is bound to the patient it belongs to, so draining
      // after a navigation cannot write B's form onto A's id.
      if (isSavingRef.current) {
        pendingSaveRef.current = {
          patientId: targetPatientId,
          form: formSnapshot,
          force: opts?.force,
          phoneFieldsChangedWhileSaving: Array.from(
            phoneFieldsChangedWhileSavingRef.current
          ),
          mrnChangedWhileSaving: mrnChangedWhileSavingRef.current,
        };
        return;
      }

      const savingGeneration = editGenerationRef.current;
      const savingPatientId = targetPatientId;
      isSavingRef.current = true;
      // Any phone edits accumulated since the previous request began belong to
      // this queued snapshot. New edits will be tracked for the next request.
      const explicitlyChangedPhoneFields =
        opts?.phoneFieldsChangedWhileSaving ?? [];
      const explicitlyChangedMrn = opts?.mrnChangedWhileSaving ?? false;
      phoneFieldsChangedWhileSavingRef.current.clear();
      mrnChangedWhileSavingRef.current = false;
      takePendingSave(pendingSaveRef);
      if (mountedRef.current) {
        setSaveStatus('saving');
      }
      try {
        const body: PatientUpdate = {
          ...getChangedMrnUpdate(
            formSnapshot,
            lastSavedFormRef.current,
            explicitlyChangedMrn
          ),
          first_name: formSnapshot.first_name.trim(),
          last_name: formSnapshot.last_name.trim(),
          middle_name: formSnapshot.middle_name.trim() || null,
          suffix: formSnapshot.suffix.trim() || null,
          date_of_birth: formSnapshot.date_of_birth
            ? formatDateToISO(formSnapshot.date_of_birth)
            : null,
          gender: formSnapshot.gender || null,
          marital_status: formSnapshot.marital_status || null,
          employment_status: formSnapshot.employment_status || null,
          primary_language: formSnapshot.primary_language.trim() || null,
          ssn: formSnapshot.ssn.replace(/\D/g, '') || null,
          mbi: formSnapshot.mbi.trim() || null,
          ...getChangedPhoneUpdates(
            formSnapshot,
            lastSavedFormRef.current,
            explicitlyChangedPhoneFields
          ),
          email: formSnapshot.email.trim() || null,
          address: formSnapshot.address.trim() || null,
          city: formSnapshot.city.trim() || null,
          state: formSnapshot.state || null,
          zip_code: formSnapshot.zip_code.trim() || null,
          county: formSnapshot.county.trim() || null,
          emergency_contact: formSnapshot.emergency_contact.trim() || null,
          emergency_contact_relationship:
            formSnapshot.emergency_contact_relationship.trim() || null,
          secondary_contact: formSnapshot.secondary_contact.trim() || null,
          secondary_contact_relationship:
            formSnapshot.secondary_contact_relationship.trim() || null,
          needs_legal_representative: formSnapshot.needs_legal_representative,
          has_poa: formSnapshot.has_poa,
        };

        const mrnBeforeSave = lastSavedFormRef.current.mrn;

        const response = await updatePatient({
          path: { patient_id: savingPatientId },
          body,
        });

        if (patientIdRef.current !== savingPatientId) {
          return;
        }

        const savedMrn = response?.data?.mrn ?? '';
        if (
          response?.data &&
          'mrn' in body &&
          savedMrn &&
          savedMrn !== mrnBeforeSave
        ) {
          // The save deliberately left OASIS M0020 alone, so ask whether the
          // charts that still answer with the old MRN should follow. Gated on
          // this request having carried an MRN, not just on the response
          // differing: someone else changing the MRN while this tab sits open
          // would otherwise pop the offer on a save of the address, naming a
          // change this user did not make. Also skipped when the MRN was
          // cleared, since there is nothing to write into an answered M0020
          // except a blank, which would erase it.
          void offerM0020Sync(savingPatientId, savedMrn);
        }

        const responseIsStale =
          pendingSaveRef.current != null ||
          editGenerationRef.current !== savingGeneration;
        const applyToForm = mountedRef.current && !responseIsStale;

        if (response?.data) {
          // Echo when this save is current, or after unmount so a remount
          // hydrates from what was persisted. Skip the echo while still
          // mounted and typing so the header does not snap to an older value.
          if (applyToForm || !mountedRef.current) {
            onPatientUpdated(response.data);
          }
          if (!applyToForm) {
            return;
          }
          const canonical = patientToForm(response.data);
          lastSavedFormRef.current = canonical;
          skipAutosaveRef.current = true;
          setForm(canonical);
          setSaveStatus('saved');
          // Only fall back to idle if a newer edit hasn't already moved on.
          setTimeout(
            () => setSaveStatus(s => (s === 'saved' ? 'idle' : s)),
            2000
          );
        } else if (mountedRef.current) {
          setSaveStatus('error');
          showError(
            getUserFacingApiMessage(
              response,
              'Failed to save patient information'
            )
          );
          setTimeout(
            () => setSaveStatus(s => (s === 'error' ? 'idle' : s)),
            3000
          );
        }
      } catch (err) {
        if (!mountedRef.current) {
          return;
        }
        if (patientIdRef.current !== savingPatientId) {
          return;
        }
        if (editGenerationRef.current !== savingGeneration) {
          return;
        }
        console.error('Failed to save patient information:', err);
        setSaveStatus('error');
        showError('Failed to save patient information');
        setTimeout(
          () => setSaveStatus(s => (s === 'error' ? 'idle' : s)),
          3000
        );
      } finally {
        isSavingRef.current = false;
        const pending = takePendingSave(pendingSaveRef);
        if (pending && pending.patientId === patientIdRef.current) {
          await doSave(pending.form, pending);
        }
      }
    },
    [canEditPatientInfo, isDirty, offerM0020Sync, onPatientUpdated, showError]
  );

  // Keep the debounce effect off `doSave`'s identity. Parent re-renders used
  // to rebuild `onPatientUpdated` (an inline lambda), which rebuilt `doSave`,
  // re-ran this effect, and bumped generation over a still-valid in-flight
  // save — skipping `onPatientUpdated` and retriggering the debounce.
  const doSaveRef = useRef(doSave);
  doSaveRef.current = doSave;

  // Tab switch and patient navigation unmount this component. Flush the
  // latest snapshot so a debounce timer (or extra keystrokes during an
  // in-flight PUT) is not dropped, which would leave the parent stale and
  // let the next remount autosave overwrite the value already saved.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      const snapshot = latestFormRef.current;
      if (!snapshot.first_name.trim() || !snapshot.last_name.trim()) {
        return;
      }
      const dirty = formsDiffer(snapshot, lastSavedFormRef.current);
      if (isSavingRef.current) {
        pendingSaveRef.current = {
          patientId: patientIdRef.current,
          form: snapshot,
          force: !dirty,
          phoneFieldsChangedWhileSaving: Array.from(
            phoneFieldsChangedWhileSavingRef.current
          ),
          mrnChangedWhileSaving: mrnChangedWhileSavingRef.current,
        };
        return;
      }
      if (dirty) {
        void doSaveRef.current(snapshot);
      }
    };
  }, []);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    if (!canEditPatientInfo) {
      setSaveStatus('idle');
      return;
    }

    // Bump on every user edit, including a revert or a temporarily invalid
    // form, so an in-flight response cannot restore the abandoned value.
    editGenerationRef.current += 1;

    const hasRequiredNames = Boolean(
      form.first_name.trim() && form.last_name.trim()
    );
    const hasValidPhones = hasValidChangedPhones(
      form,
      lastSavedFormRef.current
    );
    const hasValidMrn = hasValidChangedMrn(form, lastSavedFormRef.current);
    const willSave =
      hasRequiredNames && hasValidPhones && hasValidMrn && isDirty(form);
    if (!willSave) {
      setSaveStatus(s => (s === 'unsaved' ? 'idle' : s));
      if (isSavingRef.current || pendingSaveRef.current != null) {
        pendingSaveRef.current = hasRequiredNames
          ? {
              patientId: patientIdRef.current,
              form,
              force: !isDirty(form),
              phoneFieldsChangedWhileSaving: Array.from(
                phoneFieldsChangedWhileSavingRef.current
              ),
              mrnChangedWhileSaving: mrnChangedWhileSavingRef.current,
            }
          : null;
      }
      return;
    }

    setSaveStatus('unsaved');
    setChangeToken(t => t + 1);

    debounceTimer.current = setTimeout(() => {
      doSaveRef.current(latestFormRef.current);
    }, CHART_AUTOSAVE_DELAY_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [canEditPatientInfo, form, isDirty]);

  const updateField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    if (isSavingRef.current && PHONE_FIELDS.includes(key as PhoneField)) {
      phoneFieldsChangedWhileSavingRef.current.add(key as PhoneField);
    }
    if (isSavingRef.current && key === 'mrn') {
      mrnChangedWhileSavingRef.current = true;
    }
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCaseManagerChange = async (value: string) => {
    const previousId = currentCaseManagerId;
    setCurrentCaseManagerId(value);
    try {
      const response = await updateCurrentEpisodeCaseManager({
        path: { patient_id: patient.id },
        body: { case_manager_id: value },
      });
      if (!response?.data) {
        setCurrentCaseManagerId(previousId);
        showError(
          getUserFacingApiMessage(response, 'Failed to update case manager')
        );
      }
    } catch {
      setCurrentCaseManagerId(previousId);
      showError('Failed to update case manager');
    }
  };

  const handlePhysicianChange = async (value: string) => {
    if (!episodeId) return;
    const previousId = currentPhysicianId;
    setCurrentPhysicianId(value);
    try {
      const response = await updateEpisodeAssignments({
        path: { episode_id: episodeId },
        body: { clinic_physician_id: value },
      });
      if (!response?.data) {
        setCurrentPhysicianId(previousId);
        showError('Failed to update physician');
      } else {
        // Episode saved — offer to carry the same physician onto the NOA, but
        // only when there is an unsent NOA whose physician actually differs.
        // A failed check just skips the popup; the episode save already stuck.
        try {
          const check = await checkEpisodeNoaPhysicianSync({
            path: { episode_id: episodeId },
            query: { clinic_physician_id: value },
          });
          if (check.data?.needs_update) {
            setNoaSyncPhysicianId(value);
          }
        } catch (err: unknown) {
          console.error('Failed to check NOA physician sync:', err);
        }
      }
    } catch {
      setCurrentPhysicianId(previousId);
      showError('Failed to update physician');
    }
  };

  const handleConfirmNoaPhysicianSync = async () => {
    if (!episodeId || !noaSyncPhysicianId) return;
    setIsSyncingNoaPhysician(true);
    try {
      const response = await syncEpisodeNoaPhysician({
        path: { episode_id: episodeId },
        body: { clinic_physician_id: noaSyncPhysicianId },
      });
      if (response.error || !response.data) {
        console.error('Failed to update NOA physician:', response.error);
        showError('Failed to update the NOA physician');
      } else if (response.data.updated) {
        showSuccess('NOA physician updated');
      } else {
        // Race guard: between the check and this confirm, the NOA may have
        // been sent or already updated to this physician by someone else.
        showSuccess('NOA already up to date — nothing was changed');
      }
    } catch (err: unknown) {
      console.error('Failed to update NOA physician:', err);
      showError('Failed to update the NOA physician');
    } finally {
      setIsSyncingNoaPhysician(false);
      setNoaSyncPhysicianId(null);
    }
  };

  // Build physician dropdown options from the clinic roster. If the episode's
  // current physician isn't in the active roster (e.g. soft-deleted), prepend a
  // synthetic option so the assignment still renders rather than going blank.
  const physicianOptions = physicians.map(p => ({
    value: p.id,
    label: formatPhysicianName(p),
  }));
  if (
    currentPhysicianId &&
    !physicianOptions.some(o => o.value === currentPhysicianId)
  ) {
    physicianOptions.unshift({
      value: currentPhysicianId,
      label: physicianName ?? 'Current physician',
    });
  }

  // The roster entry behind the current assignment, for the details popover.
  // Stays null for the synthetic option above: a physician off the roster has
  // no record to read metadata from, so the popover renders nothing.
  const selectedPhysician = useMemo(
    () => physicians.find(p => p.id === currentPhysicianId) ?? null,
    [physicians, currentPhysicianId]
  );

  return (
    <div className='space-y-6 max-w-4xl'>
      {/* Auto-save status pill: a zero-height sticky overlay that floats over
          the top-right of the form and stays in view while it scrolls. Zero
          height means showing/hiding the pill never shifts the layout. */}
      <div className='pointer-events-none sticky top-0 z-20 flex h-0 justify-end'>
        <AutoSavePill
          status={saveStatus}
          debounceMs={CHART_AUTOSAVE_DELAY_MS}
          changeToken={changeToken}
        />
      </div>

      {/* Demographic Information */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <User className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Demographic Information</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='first_name'>
                First Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='first_name'
                value={form.first_name}
                onChange={e => updateField('first_name', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='middle_name'>Middle Name</Label>
              <Input
                id='middle_name'
                value={form.middle_name}
                onChange={e => updateField('middle_name', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='last_name'>
                Last Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='last_name'
                value={form.last_name}
                onChange={e => updateField('last_name', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='suffix'>Suffix</Label>
              <Input
                id='suffix'
                value={form.suffix}
                onChange={e => updateField('suffix', e.target.value)}
                disabled={!canEditPatientInfo}
                placeholder='Jr, Sr, III'
                maxLength={MAX_LENGTHS.suffix}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Date of Birth</Label>
              <DatePicker
                date={form.date_of_birth}
                onDateChange={d => updateField('date_of_birth', d)}
                disabled={!canEditPatientInfo}
                datePickerSize='sm'
                className='w-full'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Gender</Label>
              <Select
                value={form.gender}
                onValueChange={v => updateField('gender', v as Gender)}
              >
                <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                  <SelectValue placeholder='Select gender' />
                </SelectTrigger>
                <SelectContent>
                  {GENDER_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='ssn'>SSN</Label>
              <Input
                id='ssn'
                value={form.ssn}
                onChange={e => updateField('ssn', formatSSN(e.target.value))}
                disabled={!canEditPatientInfo}
                placeholder='XXX-XX-XXXX'
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='mbi'>MBI</Label>
              <Input
                id='mbi'
                value={form.mbi}
                onChange={e => updateField('mbi', e.target.value)}
                disabled={!canEditPatientInfo}
                placeholder='1EG4-TE5-MK72'
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='mrn'>MRN</Label>
              <Input
                id='mrn'
                value={form.mrn}
                onChange={e => updateField('mrn', e.target.value)}
                disabled={!canEditPatientInfo}
                maxLength={MAX_LENGTHS.mrn}
                inputSize='sm'
                aria-invalid={!isValidMrn(form.mrn) || undefined}
                aria-describedby={
                  !isValidMrn(form.mrn) ? 'mrn-error' : undefined
                }
              />
              {!isValidMrn(form.mrn) && (
                <p
                  id='mrn-error'
                  className='mt-1 text-xs text-destructive'
                  role='alert'
                >
                  {MRN_ERROR_MESSAGE}
                </p>
              )}
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='primary_language'>Primary Language</Label>
              <Input
                id='primary_language'
                value={form.primary_language}
                onChange={e => updateField('primary_language', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Marital Status</Label>
              <Select
                value={form.marital_status || NONE_VALUE}
                onValueChange={v =>
                  updateField(
                    'marital_status',
                    v === NONE_VALUE ? '' : (v as MaritalStatus)
                  )
                }
              >
                <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                  <SelectValue placeholder='Select marital status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>Not specified</SelectItem>
                  {MARITAL_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label>Employment Status</Label>
              <Select
                value={form.employment_status || NONE_VALUE}
                onValueChange={v =>
                  updateField(
                    'employment_status',
                    v === NONE_VALUE ? '' : (v as EmploymentStatus)
                  )
                }
              >
                <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                  <SelectValue placeholder='Select employment status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>Not specified</SelectItem>
                  {EMPLOYMENT_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <Phone className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Contact Information</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='phone'>Phone</Label>
              <Input
                id='phone'
                value={form.phone}
                onChange={e =>
                  updateField('phone', formatUsPhoneEntry(e.target.value))
                }
                disabled={!canEditPatientInfo}
                inputSize='sm'
                aria-invalid={!isCompleteUsPhone(form.phone) || undefined}
                aria-describedby={
                  !isCompleteUsPhone(form.phone) ? 'phone-error' : undefined
                }
              />
              {!isCompleteUsPhone(form.phone) && (
                <p
                  id='phone-error'
                  className='mt-1 text-xs text-destructive'
                  role='alert'
                >
                  {PHONE_ERROR_MESSAGE}
                </p>
              )}
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='email'>Email</Label>
              <Input
                id='email'
                type='email'
                value={form.email}
                onChange={e => updateField('email', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='md:col-span-3 space-y-1.5'>
              <Label htmlFor='address'>Street Address</Label>
              <Input
                id='address'
                value={form.address}
                onChange={e => updateField('address', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='city'>City</Label>
              <Input
                id='city'
                value={form.city}
                onChange={e => updateField('city', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>State</Label>
              <Select
                value={form.state}
                onValueChange={v => updateField('state', v)}
              >
                <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                  <SelectValue placeholder='Select state' />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(st => (
                    <SelectItem key={st.value} value={st.value}>
                      {st.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='zip_code'>ZIP Code</Label>
              <Input
                id='zip_code'
                value={form.zip_code}
                onChange={e => updateField('zip_code', e.target.value)}
                disabled={!canEditPatientInfo}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='county'>County</Label>
              <Input
                id='county'
                value={form.county}
                onChange={e => updateField('county', e.target.value)}
                disabled={!canEditPatientInfo}
                maxLength={MAX_LENGTHS.county}
                inputSize='sm'
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency and secondary contacts: who to call when the patient's own
          number does not reach them. */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <Users className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Contacts</h3>
          </div>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='space-y-3'>
            <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Emergency Contact
            </p>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='space-y-1.5'>
                <Label htmlFor='emergency_contact'>Name</Label>
                <Input
                  id='emergency_contact'
                  value={form.emergency_contact}
                  onChange={e =>
                    updateField('emergency_contact', e.target.value)
                  }
                  disabled={!canEditPatientInfo}
                  maxLength={MAX_LENGTHS.contactName}
                  inputSize='sm'
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='emergency_contact_relationship'>
                  Relationship
                </Label>
                <Input
                  id='emergency_contact_relationship'
                  value={form.emergency_contact_relationship}
                  onChange={e =>
                    updateField(
                      'emergency_contact_relationship',
                      e.target.value
                    )
                  }
                  disabled={!canEditPatientInfo}
                  placeholder='Daughter, spouse, neighbor'
                  maxLength={MAX_LENGTHS.contactRelationship}
                  inputSize='sm'
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='emergency_phone'>Phone</Label>
                <Input
                  id='emergency_phone'
                  value={form.emergency_phone}
                  onChange={e =>
                    updateField(
                      'emergency_phone',
                      formatUsPhoneEntry(e.target.value)
                    )
                  }
                  disabled={!canEditPatientInfo}
                  inputSize='sm'
                  aria-invalid={
                    !isCompleteUsPhone(form.emergency_phone) || undefined
                  }
                  aria-describedby={
                    !isCompleteUsPhone(form.emergency_phone)
                      ? 'emergency_phone-error'
                      : undefined
                  }
                />
                {!isCompleteUsPhone(form.emergency_phone) && (
                  <p
                    id='emergency_phone-error'
                    className='mt-1 text-xs text-destructive'
                    role='alert'
                  >
                    {PHONE_ERROR_MESSAGE}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='space-y-3 border-t pt-6'>
            <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Secondary Contact
            </p>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div className='space-y-1.5'>
                <Label htmlFor='secondary_contact'>Name</Label>
                <Input
                  id='secondary_contact'
                  value={form.secondary_contact}
                  onChange={e =>
                    updateField('secondary_contact', e.target.value)
                  }
                  disabled={!canEditPatientInfo}
                  maxLength={MAX_LENGTHS.contactName}
                  inputSize='sm'
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='secondary_contact_relationship'>
                  Relationship
                </Label>
                <Input
                  id='secondary_contact_relationship'
                  value={form.secondary_contact_relationship}
                  onChange={e =>
                    updateField(
                      'secondary_contact_relationship',
                      e.target.value
                    )
                  }
                  disabled={!canEditPatientInfo}
                  placeholder='Daughter, spouse, neighbor'
                  maxLength={MAX_LENGTHS.contactRelationship}
                  inputSize='sm'
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='secondary_phone'>Phone</Label>
                <Input
                  id='secondary_phone'
                  value={form.secondary_phone}
                  onChange={e =>
                    updateField(
                      'secondary_phone',
                      formatUsPhoneEntry(e.target.value)
                    )
                  }
                  disabled={!canEditPatientInfo}
                  inputSize='sm'
                  aria-invalid={
                    !isCompleteUsPhone(form.secondary_phone) || undefined
                  }
                  aria-describedby={
                    !isCompleteUsPhone(form.secondary_phone)
                      ? 'secondary_phone-error'
                      : undefined
                  }
                />
                {!isCompleteUsPhone(form.secondary_phone) && (
                  <p
                    id='secondary_phone-error'
                    className='mt-1 text-xs text-destructive'
                    role='alert'
                  >
                    {PHONE_ERROR_MESSAGE}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='space-y-3 border-t pt-6'>
            <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Representation
            </p>
            <div className='flex flex-col gap-3 sm:flex-row sm:gap-8'>
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='needs_legal_representative'
                  checked={form.needs_legal_representative}
                  disabled={!canEditPatientInfo}
                  onCheckedChange={checked =>
                    updateField('needs_legal_representative', checked === true)
                  }
                />
                <Label
                  htmlFor='needs_legal_representative'
                  className='font-normal'
                >
                  Needs legal representative
                </Label>
              </div>
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='has_poa'
                  checked={form.has_poa}
                  disabled={!canEditPatientInfo}
                  onCheckedChange={checked =>
                    updateField('has_poa', checked === true)
                  }
                />
                <Label htmlFor='has_poa' className='font-normal'>
                  Has power of attorney
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Episode */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <MapPin className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Current Episode</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <div className='space-y-1.5'>
              <Label>Case Manager</Label>
              <ClinicianSelect
                key={patient.id}
                label=''
                selectSize='sm'
                placeholder={
                  !episodeLoaded ? 'Loading...' : 'Select case manager'
                }
                clinicians={caseManagers}
                defaultValue={currentCaseManagerId ?? ''}
                disabled={!episodeLoaded}
                onChange={e => handleCaseManagerChange(e.target.value)}
                isClearable={false}
                menuPosition='absolute'
              />
            </div>
            <div className='space-y-1.5'>
              {/* h-6 matches the line box a bare inline Label occupies in the
                  sibling cells, so making room for the icon does not lift this
                  column's picker above the others. */}
              <div className='flex h-6 items-center gap-1'>
                <Label>Physician</Label>
                <PhysicianInfoPopover physician={selectedPhysician} />
              </div>
              <VirtualizedFormSelect
                key={patient.id}
                label=''
                selectSize='sm'
                placeholder={!episodeLoaded ? 'Loading...' : 'Select physician'}
                options={physicianOptions}
                sortOptions
                defaultValue={currentPhysicianId ?? ''}
                disabled={!episodeLoaded}
                onChange={e => handlePhysicianChange(e.target.value)}
                isClearable={false}
                menuPosition='absolute'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode Start Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeStartDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode End Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeEndDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Imported Chart — only on imported charts; the card itself is the
          residual indicator after the badge is demoted */}
      {patient.import_source && (
        <PatientImportBadgeToggle
          patientId={patient.id}
          importedAt={patient.imported_at}
          badgeHidden={!!patient.import_badge_hidden_at}
          onUpdated={onPatientUpdated}
        />
      )}

      {/* Test / Practice Patient — last section; explains itself before the toggle */}
      <PatientTestStatusToggle
        patientId={patient.id}
        isTest={!!patient.is_test_patient}
        onUpdated={onPatientUpdated}
      />

      {/* Offered after a physician save; only an unsent NOA is ever changed */}
      <ConfirmationDialog
        open={!!noaSyncPhysicianId}
        title='Update NOA physician?'
        message="Also update the physician on this patient's Notice of Admission? A NOA that has already been sent will not be changed."
        confirmLabel='Yes, update NOA'
        cancelLabel='No'
        loading={isSyncingNoaPhysician}
        onAccept={() => void handleConfirmNoaPhysicianSync()}
        onCancel={() => setNoaSyncPhysicianId(null)}
      />

      {/* Offered after an MRN change; declining leaves the charts as they are,
          and the OASIS review flags the mismatch until someone updates them */}
      <ConfirmationDialog
        open={!!m0020Sync}
        title='Update OASIS Patient ID?'
        message={m0020SyncMessage(m0020Sync)}
        confirmLabel='Yes, update OASIS'
        cancelLabel='No'
        loading={isSyncingM0020}
        onAccept={() => void handleConfirmM0020Sync()}
        onCancel={() => setM0020Sync(null)}
      />
    </div>
  );
};
